import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { analyticsService, IntentDistEntry } from '../../services/analyticsService';

interface BuyerIntentPieChartProps {
  businessIdentifier?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const INTENT_COLORS: Record<string, string> = {
  'Self Use': '#4A90D9',
  'Investment': '#48BB78',
  'Mixed': '#ED8936',
  'Unknown': '#A0AEC0',
};

const BuyerIntentPieChart: React.FC<BuyerIntentPieChartProps> = ({
  businessIdentifier,
}) => {
  const [data, setData] = useState<IntentDistEntry[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!businessIdentifier) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await analyticsService.getBuyerIntentDistribution(businessIdentifier);
        setData(result.data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch buyer intent data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier]);

  const getChartOption = () => {
    if (!data || data.length === 0) return {};

    const defaultColors = ['#4A90D9', '#48BB78', '#ED8936', '#A0AEC0', '#9F7AEA', '#F56565'];
    const pieData = data.map((entry, idx) => ({
      value: entry.count,
      name: entry.intent_type,
      itemStyle: {
        color: INTENT_COLORS[entry.intent_type] || defaultColors[idx % defaultColors.length],
      },
    }));

    return {
      tooltip: {
        trigger: 'item' as const,
        textStyle: { fontFamily: FONT_FAMILY },
        formatter: (params: any) => {
          return `<b>${params.name}</b><br/>Count: ${params.value}<br/>Share: ${params.percent?.toFixed(1)}%`;
        },
      },
      legend: {
        bottom: 0,
        left: 'center',
        type: 'scroll' as const,
        textStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#718096' },
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 12,
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: '{b}\n{d}%',
            fontFamily: FONT_FAMILY,
            fontSize: 10,
            color: '#4A5568',
            lineHeight: 14,
          },
          labelLine: {
            show: true,
            length: 8,
            length2: 12,
          },
          emphasis: {
            label: { show: true, fontSize: 12, fontWeight: 'bold' },
          },
          data: pieData,
        },
      ],
    };
  };

  const hasData = data && data.length > 0;

  return (
    <ChartCard title="Buyer Intent Type" isLoading={isLoading} error={error}>
      {hasData ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '340px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            Coming Soon — Data will appear as new conversations are processed
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default BuyerIntentPieChart;
